import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SAGE = "https://images.pexels.com/photos/3020635/pexels-photo-3020635.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = u.role === "grocery_store" ? "/store" : u.role === "aid_organization" ? "/organization" : "/admin";
      navigate(dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={SAGE} alt="Sage leaves" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-900/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <Logo textClass="text-white" />
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-semibold tracking-tight leading-tight">Welcome back.</h2>
            <p className="text-emerald-100/80 mt-4 max-w-sm">Log in to manage rescued food and keep the redistribution loop moving.</p>
          </div>
          <span />
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-stone-50">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 lg:hidden mb-8">
            <Logo />
          </Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Log in</h1>
          <p className="text-stone-500 mt-2">For grocery stores & aid organizations.</p>

          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-4 text-sm text-stone-600">
            <Info className="w-4 h-4 text-emerald-800 mt-0.5 shrink-0" />
            <span>
              Accounts are only for <span className="font-medium text-stone-900">grocery stores</span> and <span className="font-medium text-stone-900">aid organizations</span>.
              If you need help or want to donate an item, no login is required —{" "}
              <Link to="/near-me" className="text-emerald-800 underline underline-offset-2">find aid</Link> or{" "}
              <Link to="/help" className="text-emerald-800 underline underline-offset-2">donate now</Link>.
            </span>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-stone-500">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@store.com" className="rounded-xl border-stone-200 bg-white h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-stone-500">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-xl border-stone-200 bg-white h-11" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 h-11 text-white">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <p className="text-sm text-stone-500 mt-6">
            New here?{" "}
            <Link to="/register" className="text-emerald-800 font-medium hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
